<template>
  <b-card
    v-if="order"
    :id="order.id"
    no-body
    class="invoice-preview-card pagebreak delivery-note-print-card"
    :class="`delivery-note-print-card--${printMode}`"
  >
    <div class="d-flex justify-content-between flex-wrap mr-1 ml-1 delivery-note-header">
      <div class="invoice-preview">
        <h1
          class="mt-1 dn-title"
          style="color: #0A2634; font-size: 30px"
          aria-label="En-tête du bon de livraison"
        >
          Bon de livraison
          <span
            class="invoice-title invoice-number"
            style="color: #0A2634;"
          >
            N° : BL-{{ order.id }}
          </span>
        </h1>
        <h3
          class="dn-date"
          style="color: black"
        >
          Date :
          <span
            class="invoice-date"
            style="color: black"
          >
            {{ formatFn(order.created_at) }}</span>
        </h3>
        <div class="mt-1 company-meta">
          <b-card-text
            class="h4"
            style="color: black"
          >
            <strong style="color:#0A2634">{{ companyBordereauValue('name', company.name) }} :</strong> {{ company.tagline || 'Société de logistique et livraison de colis' }}
          </b-card-text>
          <b-card-text
            class="h4"
            style="color: black"
          >
            <strong style="color:#0A2634">Matricule fiscale :</strong> {{ companyBordereauValue('tax_identification_number', maskedTaxNumber) }}
          </b-card-text>
          <b-card-text
            class="h4"
            style="color: black"
          >
            <strong style="color:#0A2634">Adresse :</strong> {{ companyBordereauValue('address', company.address) }}
          </b-card-text>
          <b-card-text
            class="h4"
            style="color: black"
          >
            <strong style="color:#0A2634">Téléphone :</strong> {{ companyBordereauValue('phone_number', companyPhoneValue) }}
          </b-card-text>
        </div>
      </div>

      <div style="text-align:end">
        <div>
          <img
            src="/logo.png"
            width="180px"
            height="auto"
            alt="Logo de la société de livraison"
            class="logo-top"
          >
        </div>
        <div class="d-flex justify-content-end align-items-start mt-0 codes-right">
          <div class="header-qr-wrap">
            <qrcode-vue
              :value="headerBarcodeValue"
              :size="headerQrSize"
              level="H"
            />
            <span
              class="header-qr-id"
              aria-label="Identifiant code-barres"
            >{{ headerBarcodeValue }}</span>
          </div>
          <div class="header-barcode-wrap">
            <barcode
              :value="headerBarcodeValue"
              :height="78"
              :width="2.1"
              :font-size="28"
              font-options="bold"
              :text-margin="2"
              :margin="0"
              :display-value="true"
              class="barcode-small"
              aria-label="Code-barres de la commande"
            />
          </div>
        </div>
      </div>
    </div>

    <b-card-text
      v-if="isFlagEnabled(order.is_fragile)"
      class="status-banner status-banner--fragile"
      aria-label="Colis fragile"
    >
      <strong>Fragile</strong>
    </b-card-text>

    <div
      v-if="order.repository"
      class="dn-agence-line mr-1 ml-1 mb-1"
    >
      <strong>{{ order.repository.name }}</strong>
      &nbsp;&gt;&gt;------- dispatch -------&gt;&gt;&nbsp;
      <strong>{{ dispatchGovernorateName(order) }}</strong>
    </div>

    <div class="d-flex flex-row flex-wrap justify-content-between dn-info-row mr-1 ml-1 mb-1">
      <div class="dn-info-col dn-info-col--client">
        <div
          class="client-info-box"
          style="border: 2px solid black; font-size: 18px; min-width: 380px;"
        >
          <b-card-text class="ml-2 mr-2 mt-2 mb-1">
            <span style="color: #1565C0">Destinataire : </span>
            <strong style="font-weight: 900; color: #000;">{{ order.name }}</strong>
          </b-card-text>
          <b-card-text class="ml-2 mr-2 mb-1">
            <span style="color: #1565C0">Gouvernorat : </span>
            <strong style="font-weight: 900; color: #000;">
              {{ locationName(order.governorate) }}
              <span
                v-if="locationName(order.delegation)"
                style="color:rgb(12, 5, 5)"
              > , {{ locationName(order.delegation) }}</span>
              <span v-if="locationName(order.locality)"> , {{ locationName(order.locality) }}</span>
            </strong>
          </b-card-text>
          <b-card-text class="ml-2 mr-2 mb-1">
            <span style="color: #1565C0">Adresse : </span>
            <strong style="font-weight: 900; color: #000;">{{ order.address }}</strong>
          </b-card-text>
          <b-card-text class="ml-2 mr-2 mb-1">
            <span style="color: #1565C0">Téléphone : </span>
            <strong style="font-weight: 900; color: #000;">{{ order.phone1 }}<span v-if="order.phone2"> / {{ order.phone2 }}</span></strong>
          </b-card-text>
        </div>
      </div>

      <div class="dn-info-col dn-info-col--supplier">
        <div
          v-if="order.supplier"
          class="supplier-info-box"
          style="border: 2px solid black; font-size: 18px; min-width: 250px;"
        >
          <b-card-text class="ml-2 mr-2 mt-2 mb-1">
            <span style="color: #1565C0">Expéditeur : </span>
            <strong style="font-weight: 900; color: #000;">
              {{ supplierBordereauValue('username', order.supplier.page_name || order.supplier.username) }}
              <span
                v-if="order.supplier.is_external"
                class="badge badge-info ml-2"
              >{{ order.supplier.source_company }}</span>
            </strong>
          </b-card-text>
          <b-card-text class="ml-2 mr-2 mb-1">
            <span style="color: #1565C0">Téléphone : </span>
            <strong style="font-weight: 900; color: #000;">{{ supplierBordereauValue('phone_number', supplierPhoneDisplay) }}</strong>
          </b-card-text>
          <b-card-text class="ml-2 mr-2 mb-1">
            <span style="color: #1565C0">Adresse : </span>
            <strong style="font-weight: 900; color: #000;">{{ supplierBordereauValue('address', order.supplier.address) }}</strong>
          </b-card-text>
          <b-card-text
            v-if="order.supplier.external_barcode"
            class="ml-2 mr-2 mb-1"
          >
            <span style="color: #1565C0">Code-barres externe : </span>
            <strong style="font-weight: 900; color: #000;">{{ order.supplier.external_barcode }}</strong>
          </b-card-text>
          <b-card-text
            v-if="supplierIdentityField"
            class="ml-2 mr-2 mb-1"
          >
            <span style="color: #1565C0">{{ supplierIdentityField.label }} : </span>
            <strong style="font-weight: 900; color: #000;">{{ supplierIdentityField.value }}</strong>
          </b-card-text>
        </div>
      </div>
    </div>

    <div class="mr-1 ml-1 delivery-note-table">
      <b-table-lite
        class="ta"
        style="font-size: 20px; border: 2px solid black"
        responsive
        :items="products"
        :fields="['désignation', 'quantité']"
      />
    </div>

    <div class="mr-1 ml-1 dn-after-table-row">
      <div class="dn-after-table-left">
        <h1
          class="dn-comment-line"
          aria-label="Commentaire du bon de livraison"
        >
          Commentaire :
          <span class="dn-comment-text">{{ order.note }}</span>
        </h1>
        <div class="dn-flags-list">
          <b-card-text
            v-if="isFlagEnabled(order.is_an_exchange)"
            class="h4"
            style="color:black"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="18"
              class="mr-50"
              style="color: #5A8DEE;"
            />
            <strong style="color:#1565C0">Échange :</strong> OUI
          </b-card-text>
          <b-card-text
            v-if="isFlagEnabled(order.is_fragile)"
            class="h4"
            style="color:black"
          >
            <feather-icon
              icon="AlertTriangleIcon"
              size="18"
              class="mr-50"
              style="color: #FDAC41;"
            />
            <strong style="color:#1565C0">Fragile :</strong> OUI
          </b-card-text>
          <b-card-text
            v-if="isFlagEnabled(order.is_discharge)"
            class="h4"
            style="color:black"
          >
            <feather-icon
              icon="FileTextIcon"
              size="18"
              class="mr-50"
              style="color: #A3A1FB;"
            />
            <strong style="color:#1565C0">Décharge :</strong> OUI
          </b-card-text>
          <b-card-text
            class="h4"
            style="color:black"
          >
            <feather-icon
              icon="PackageIcon"
              size="18"
              class="mr-50"
              style="color: #C59C6C;"
            />
            <strong style="color:#1565C0">ouvrir colis :</strong> {{ isFlagEnabled(order.can_open_package) ? 'OUI' : 'NON' }}
          </b-card-text>
        </div>
      </div>
      <div class="dn-after-table-right">
        <div class="dn-total-box">
          <table style="border: 2px solid #0A2634">
            <tr class="price-row price-row-ttc">
              <th class="text-center dn-price-ttc">
                Total TTC: {{ order.price_ttc }} DT
              </th>
            </tr>
          </table>
        </div>
      </div>
    </div>

    <div
      v-if="footerBarcodeValue"
      class="mr-1 ml-1 dn-qr-section"
    >
      <div class="footer-codes-wrap">
        <div class="footer-qr-wrap">
          <qrcode-vue
            :value="footerBarcodeValue"
            :size="footerQrSize"
            class="footer-qr"
            level="H"
          />
        </div>
        <div class="footer-barcode-wrap">
          <barcode
            :value="footerBarcodeValue"
            :height="88"
            :width="1.6"
            :font-size="24"
            font-options="bold"
            :text-margin="3"
            :margin="0"
            :display-value="true"
            class="footer-barcode"
            aria-label="Code-barres externe du bon de livraison"
          />
        </div>
      </div>
    </div>

    <div
      v-if="deliveryNotePrintNote"
      class="bl-print-note"
      dir="auto"
    >
      {{ deliveryNotePrintNote }}
    </div>
  </b-card>
</template>

<script>
import {
  BCard,
  BTableLite,
  BCardText,
} from 'bootstrap-vue'
import QrcodeVue from 'qrcode.vue'
import VueBarcode from 'vue-barcode'

export default {
  components: {
    BCard,
    BTableLite,
    BCardText,
    QrcodeVue,
    barcode: VueBarcode,
  },
  props: {
    order: {
      type: Object,
      required: true,
    },
    company: {
      type: Object,
      default: () => ({}),
    },
    products: {
      type: Array,
      default: () => [],
    },
    supplierPhoneDisplay: {
      type: String,
      default: '57 021 902 / 27 964 221',
    },
    deliveryNotePrintNote: {
      type: String,
      default: '',
    },
    printMode: {
      type: String,
      default: 'single',
      validator: value => ['single', 'bulk'].includes(value),
    },
  },
  computed: {
    headerQrSize() {
      return this.printMode === 'bulk' ? 96 : 108
    },
    footerQrSize() {
      return this.printMode === 'bulk' ? 104 : 110
    },
    normalizedBarcodeValue() {
      return value => String(value === undefined || value === null ? '' : value).trim()
    },
    deliveryNoteBarcodeMode() {
      const displayMode = this.order && this.order.delivery_note_barcode && this.order.delivery_note_barcode.mode
      if (displayMode) {
        return displayMode
      }

      const companyMode = (this.company && this.company.delivery_note_barcode_mode) || 'order_id_and_external_id'
      const isSyncRelatedOrder = Boolean(
        this.normalizedBarcodeValue(this.order && this.order.external_id)
          || (this.order && (this.order.is_outsourcing || this.order.outsourcing_company)),
      )
      return companyMode === 'external_id_only' && isSyncRelatedOrder
        ? companyMode
        : 'order_id_and_external_id'
    },
    headerBarcodeValue() {
      const display = this.order && this.order.delivery_note_barcode
      if (display && display.primary_value) {
        return this.normalizedBarcodeValue(display.primary_value)
      }

      const externalId = this.normalizedBarcodeValue(this.order && this.order.external_id)
      const orderId = this.normalizedBarcodeValue(this.order && this.order.id)
      if (this.deliveryNoteBarcodeMode === 'external_id_only') {
        return externalId || orderId
      }

      return orderId
    },
    footerBarcodeValue() {
      const display = this.order && this.order.delivery_note_barcode
      if (display && display.secondary_value) {
        return this.normalizedBarcodeValue(display.secondary_value)
      }

      if (this.deliveryNoteBarcodeMode === 'external_id_only') {
        return ''
      }

      return this.normalizedBarcodeValue(this.order && this.order.external_id)
    },
    maskedTaxNumber() {
      if (!this.company.tax_identification_number) return ''
      return `***${this.company.tax_identification_number.slice(3)}`
    },
    companyPhoneValue() {
      if (!this.company) return ''
      return [this.company.phone1, this.company.phone2].filter(Boolean).join(' / ')
    },
    supplierIdentityField() {
      const supplier = this.order && this.order.supplier
      if (!supplier) return null

      const fields = [
        {
          key: 'matricule_fiscale',
          label: 'Matricule fiscale',
          fallback: supplier.tax_identification_number,
        },
        {
          key: 'cin',
          label: 'CIN',
          fallback: supplier.cin,
        },
      ]

      const visibleField = fields.find(field => {
        const display = this.supplierBordereauDisplay(field.key)
        return display && display.show === true && (display.value || field.fallback)
      })

      const selectedField = visibleField || fields.find(field => {
        const display = this.supplierBordereauDisplay(field.key)
        return display && display.show === false
      }) || fields.find(field => field.fallback)

      if (!selectedField) return null

      return {
        label: selectedField.label,
        value: this.supplierBordereauValue(selectedField.key, selectedField.fallback),
      }
    },
  },
  methods: {
    companyBordereauValue(field, fallback = '') {
      const display = this.company
        && this.company.bordereau_display
        && this.company.bordereau_display[field]

      if (display && Object.prototype.hasOwnProperty.call(display, 'display_value')) {
        return display.display_value
      }

      return fallback || ''
    },
    supplierBordereauValue(field, fallback = '') {
      const display = this.supplierBordereauDisplay(field)

      if (display && Object.prototype.hasOwnProperty.call(display, 'display_value')) {
        return display.display_value
      }

      return fallback || ''
    },
    supplierBordereauDisplay(field) {
      return this.order
        && this.order.supplier
        && this.order.supplier.bordereau_display
        && this.order.supplier.bordereau_display[field]
    },
    formatFn(date) {
      if (!date) return null

      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      const h = date.slice(11, 19)

      return `${year}-${month.padStart(2, '0')}-${day1.padStart(2, '0')} | ${h}`
    },
    locationName(value) {
      if (!value) return ''

      if (typeof value === 'object') {
        return value.name || ''
      }

      if (typeof value === 'string') {
        const trimmedValue = value.trim()
        if (!trimmedValue) return ''

        const matches = trimmedValue.match(/\{.*\}/)
        if (matches && matches[0]) {
          try {
            const parsedValue = JSON.parse(matches[0])
            if (parsedValue && parsedValue.name) return parsedValue.name
          } catch (error) {
            return trimmedValue
          }
        }

        return trimmedValue
      }

      return ''
    },
    dispatchGovernorateName(order) {
      const governorateName = this.locationName(order ? order.governorate : null)
      if (governorateName) return governorateName
      if (order && order.destination_repository_name) return order.destination_repository_name
      if (order && order.repository && order.repository.name) return order.repository.name
      return ''
    },
    isFlagEnabled(value) {
      if (typeof value === 'boolean') return value
      if (typeof value === 'number') return value === 1
      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase()
        return normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'oui'
      }
      return false
    },
  },
}
</script>

<style lang="scss" scoped>
table,
td,
th {
  padding: 10px;
  text-align: left;
  border: 1px solid #1565C0;
}

table {
  width: 100%;
  border-collapse: collapse;
}

.delivery-note-print-card {
  display: flex;
  flex-direction: column;
}

.delivery-note-header {
  align-items: flex-start;
}

.dn-info-row {
  gap: 8px;
}

.dn-info-col {
  flex: 1 1 300px;
  min-width: 0;
}

.dn-info-col--client .client-info-box,
.dn-info-col--supplier .supplier-info-box {
  font-size: 18px;
  width: 100%;
  min-width: 0 !important;
  box-sizing: border-box;
}

.codes-right {
  margin-top: -12px;
  gap: 0;
}

.codes-right span {
  font-size: 18px;
  font-weight: 800;
  font-family: monospace;
  line-height: 1;
}

.header-qr-wrap,
.header-barcode-wrap {
  flex: 0 0 auto;
}

.header-qr-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.header-barcode-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-left: -4px;
}

.header-qr-id {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.1;
  font-weight: 700;
  font-family: monospace;
  letter-spacing: 1px;
  text-align: center;
}

.barcode-small {
  display: block;
  overflow: visible;

  svg {
    display: block !important;
    width: 390px !important;
    max-width: 390px !important;
    height: 132px !important;
    overflow: visible !important;
  }
}

.logo-top {
  margin-top: -10px;
}

.price-row {
  th {
    padding: 12px 20px;
    font-weight: 600;
    color: #1565C0;
  }

  &.price-row-ttc {
    background-color: #fff;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;

    th {
      font-size: 1.1em;
      font-weight: 700;
      color: #0A2634;
    }
  }
}

.dn-price-ttc {
  font-size: 28px !important;
  font-weight: 900 !important;
  letter-spacing: 0.5px;
}

.delivery-note-table {
  flex: 0 0 auto;
  min-height: 0;
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
}

.delivery-note-table .table,
.delivery-note-table .table-responsive {
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
}

.dn-after-table-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  margin: 4px 0 0;
  padding: 0;
  flex: 0 0 auto;
}

.dn-after-table-left {
  flex: 1 1 auto;
  min-width: 0;
}

.dn-after-table-right,
.dn-total-box {
  flex: 0 0 auto;
  align-self: flex-start;
}

.dn-qr-section {
  margin-top: auto;
  padding-bottom: 6px;
  flex: 0 0 auto;
}

.footer-codes-wrap {
  display: inline-flex;
  align-items: flex-end;
  gap: 0;
  flex-wrap: nowrap;
  page-break-inside: avoid;
  break-inside: avoid;
}

.bl-print-note {
  margin: 8px 1rem 0;
  padding: 6px 8px;
  border: 1px solid #222;
  border-radius: 2px;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
  text-align: center;
  white-space: pre-wrap;
}

.footer-qr-wrap {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  flex: 0 0 auto;
}

.footer-barcode-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  margin-left: 4px;
  flex: 0 0 auto;
}

.footer-barcode {
  display: block;
  width: 260px !important;
  max-width: 260px !important;
  height: 110px !important;
  overflow: visible;

  svg {
    display: block !important;
    width: 260px !important;
    max-width: 260px !important;
    height: 110px !important;
    overflow: visible !important;
  }
}

.dn-agence-line {
  font-size: 16px;
  font-weight: bold;
  text-align: center;
  padding: 4px 8px;
  border: 1px solid #0A2634;
  color: #0A2634;
  margin-bottom: 6px;
}

.dn-comment-line {
  margin: 0;
  color: #1565C0;
  font-size: 22px;
  font-weight: bold;
  line-height: 1.2;
}

.dn-comment-text {
  color: #000;
  font-size: 18px;
  font-weight: normal;
}

.dn-flags-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 4px;
}

.status-banner {
  margin: 4px 4px 6px;
  padding: 4px 8px;
  border: 1px solid #1d1d1b;
  text-align: center;
  line-height: 1.05;
  background: #fff;
}

.status-banner--fragile {
  font-size: 40px;
  text-transform: uppercase;
}

.status-banner--fragile strong {
  color: #bc2b18;
  font-weight: 900;
  letter-spacing: 3px;
}

.badge.badge-info {
  background-color: #17a2b8;
  color: white;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 3px;
  font-weight: normal;
}

@media (max-width: 768px) {
  .footer-codes-wrap,
  .footer-qr-wrap {
    align-items: flex-start;
  }

  .dn-info-col {
    flex: 1 1 100%;
  }

  .dn-info-col--client .client-info-box {
    font-size: 16px;
  }

  .dn-info-col--supplier .supplier-info-box {
    font-size: 12px;
    margin-left: 0 !important;
  }

  .dn-title {
    font-size: 22px !important;
  }

  .dn-date {
    font-size: 16px !important;
  }

  .company-meta .h4,
  .ta {
    font-size: 14px !important;
  }
}

@media print {
  .bl-print-note {
    margin-top: 5px !important;
    padding: 4px 6px !important;
    font-size: 11px !important;
    line-height: 1.25 !important;
  }

  .dn-info-row {
    gap: 4px !important;
  }

  .dn-info-col {
    flex: 1 1 45% !important;
    min-width: 0 !important;
  }

  .client-info-box,
  .supplier-info-box {
    width: 100% !important;
    min-width: 0 !important;
    min-height: 22mm !important;
    box-sizing: border-box !important;
  }

  .client-info-box {
    font-size: 17px !important;
    line-height: 1.28 !important;
  }

  .supplier-info-box {
    font-size: 13px !important;
    line-height: 1.28 !important;
  }

  .client-info-box .card-text,
  .supplier-info-box .card-text {
    margin-bottom: 1mm !important;
  }
}
</style>
